/**
 * 
 * Route manager
 * (c) lg2fabrique 2017
 * 
 * 
 * This route manager will look for all local links in the page and bind en event to prevent default load. 
 * It will then load the page and update the elements specified in the dynamicElsQueries array.
 * It will also update document title.
 * 
 * Use [data-route-exclude] selector to exclude links from the manager. 
 * You can also extend this class to specify your own selector.
 * 
 * This route manager will take care of updating dom elements. 
 *      - By default, it will only update the 'main' element. 
 *      - If you wish to update more, extend the class and declare your own array of dynamicElsQueries.
 *      - Please note that this will only work for the first element as it use querySelector so use unic selectors.
 *      
 *      ex :
            protected _dynamicElsQueries           : Array<string> = ['main', '.hero_main', 'footer', 'header .nav_main', 'main .nav_main']; 
 * 
 *
 * To specify animation before load and after load, the best way is to extend the class and override the protected functions as so :
        protected _onBeforeUpdate(url) {
            TweenMax.to(document.body, 0.4, {opacity:0,
                onComplete:()=> {
                    super._onBeforeUpdate(url);
                }
            });
        }

        protected _onRequestLoaded(e) {
            super._onRequestLoaded(e);
            TweenMax.to(document.body, 0.4, {opacity:1});
        }
 * 
 * 
 * You can also add a listener to the load like so :
        let routeManager = new ExtendRouteManager();
        routeManager.addListener(RouteManagerEvents.AFTER_LOAD, 
            () => {
                let body = document.body;   
                if (body.classList.contains('p_home')) {
                    this._page = new Home();
                } else if (body.classList.contains('p_project')) {       
                    this._page = new Project();
                } else {
                    // If no page js present, create basic abstract page
                    this._page = new AbstractPage();         
                }
            }
        );
 * 
 * 
 * 
*/

import UrlUtils from 'frontools/utils/UrlUtils';
import { autobind } from 'frontools/decorators/Autobind';
import Request from 'frontools/data/Request';
import { RequestConfig, RequestEvent } from 'frontools/data/Request';
import AbstractDispatcher from 'frontools/abstract/AbstractDispatcher';

export default class RouteManager extends AbstractDispatcher {
    
    private static _instance                : RouteManager;
    
    protected _linksExcludeSelector         : '[data-route-exclude]';
    protected _dynamicElsQueries            : Array<string> = ['main']; 

    private _isSupported                    : boolean            = false;
    private _isOn                           : boolean            = false;
    private _debug                          : boolean            = false;

    private _lastCalledRoute                : any;

    constructor() {
        super();
        // This class is a singleton, meaning you can only use one instance
        if(RouteManager._instance) throw new Error('Error: Use RouteManager.getInstance() instead of new.');
        RouteManager._instance = this;

        // Validating if push state is available in browser
        if ((window as any).history && !(window as any).history.pushState) return;
        else this._isSupported = true;

        // Starts routing
        this._start();
    }

    /**
     * Starts Route Manager
     * 
     * @private
     * @returns 
     * @memberof RouteManager
     */
    private _start() {
        if (!this._isSupported) return;

        this.parseLinks();
        window.onpopstate = this.onPopState.bind(this);

        // Initial replace state is important to for the on pop state event
        window.history.replaceState(window.location.href, window.location.href, window.location.href);
        this._lastCalledRoute == window.location.href;

        this._isOn = true;
    }

    /**
     * Used to bind all links in the document object to the route event
     */
    public parseLinks() {
        [].slice.call(document.querySelectorAll( 'a' )).forEach( a  => {
            // Check if link is internal and if it doesn't need to be exclude
            if (!UrlUtils.isExternalURL(a.href) && !a.getAttribute(this._linksExcludeSelector)) {
                a.removeEventListener('click', this.onClickListener); 
                a.addEventListener('click', this.onClickListener);
            }
        });
    }

    /**
     * On click handler
     * @param {Event} Mouse event
     */
    @autobind
    private onClickListener(e) {
        if(!this._isOn) return;
        if(!e.currentTarget.href) return;
       
        let url = UrlUtils.parse(e.currentTarget.href).url;

        if (url) {
            e && e.preventDefault();
            history.pushState(url, url, url);
            // history.push
            this._executeRoute(url);
        }
    }

    /**
     * Pop state history handler
     * @param {Event} History event
     */
    private onPopState(event) {
        if(!this._isOn) return;
        if (!event.state) return;

        this._executeRoute(event.state);
    }

    private _executeRoute(requestedRoute:string) {
        if (this._lastCalledRoute == requestedRoute) {
            document.documentElement.scrollTop = 0;
            return;
        }

        this._log('Route manager next route', requestedRoute);

        this._lastCalledRoute = requestedRoute;

        this._onBeforeUpdate(requestedRoute)
    }

    protected _onBeforeUpdate(url) {
        this.dispatch({type: RouteManagerEvents.BEFORE_LOAD});    
        
        // Setting scroll value to 0 to prevent page of staying in same position
        document.documentElement.scrollTop = 0;
        
        // Do update
        let config = new RequestConfig();
        config.url = url;
        let request = new Request(config);   
        request.addListener(RequestEvent.LOAD, this._onUpdate.bind(this));
    }

    protected _onUpdate(e) {
        let newDom = document.createElement('html');
        newDom.innerHTML = e.response;

        // Resetting class on body
        document.body.className = newDom.querySelector('body').className;
        document.title = newDom.querySelector('title').innerText;   

        // Resetting all unpersistant elements
        for (let i = 0, l = this._dynamicElsQueries.length; i < l; i++) {
            let el = document.querySelector(this._dynamicElsQueries[i]);
            let newEl = newDom.querySelector(this._dynamicElsQueries[i]);
            if (el) {
                if (newEl) {
                    el.innerHTML = newEl.innerHTML;
                } else {
                    el.parentElement.removeChild(el);
                }
            }
        }

        this.parseLinks();
        this.dispatch({type: RouteManagerEvents.AFTER_LOAD})
    }

    

    /**
     * Stop routing listener
     */
    public stop(){
        if (!this._isSupported) return;

        [].slice.call(document.querySelectorAll( 'a' )).forEach( a => {
            // Check if link is internal and if it doesn't need to be exclude
            if (!UrlUtils.isExternalURL(a.href) && !a.getAttribute(this._linksExcludeSelector)) {
                a.removeEventListener('click', this.onClickListener); 
            }
        });

        window.onpopstate = null;
        this._isOn = false;
    };

    /**
     * Add element in changing els array
     * @param key : Unique Query string key 
     */
    public addChangingEl(key: string) {
        if (this._dynamicElsQueries[key]) return false;
        this._dynamicElsQueries.push(key);
    }

    /**
     * Remove element from changing els array
     * @param key : Unique Query string key 
     */
    public removeChangingEl(key:string) {
        if (!this._dynamicElsQueries[key]) return false;
        delete this._dynamicElsQueries[key];
    }

    /**
     * get array of dynamic elements queries
     * @returns {string}
     */
    public get dynamicElsQueries(): Array<string> {
        return this._dynamicElsQueries;
    }

    /**
     * get array of dynamic elements queries
     * @returns {string}
     */
    public set dynamicElsQueries(dynamicElsQueries:Array<string>){
        this._dynamicElsQueries = dynamicElsQueries;
    }

    /**
     * get singleton instance
     * @returns {RouteManager}  instance's RouteManager
     */
    public static get instance(): RouteManager {
        return RouteManager._instance;
    }

    private _log(mssg: string, object:any) {
        if (this._debug) console.info(mssg, object);
    }

    public set debug(debug: boolean) {
        this._debug = debug;
    }
}

export class RouteManagerEvents {
    public static AFTER_LOAD      : string    = 'after_load';
    public static BEFORE_LOAD     : string    = 'before_load';
}

